/**
 * ContractEstimateSlice Estimate Reducers
 * 
 * Reducers for managing estimate lifecycle.
 */

import { PayloadAction } from '@reduxjs/toolkit';
import { ContractEstimateSliceState } from '../state';
import { ContractEstimate, StayType } from '../../types/contractEstimateTypes';
import { ReimbursementResult } from '../../../CalculatorService/types';

/**
 * Create a default estimate with provided company
 */
const createDefaultEstimate = (company: string): ContractEstimate => {
  const now = new Date().toISOString();
  return {
    id: crypto.randomUUID(),
    centerId: '',
    serviceDate: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD format
    payerId: '',
    stayType: 'OP', // Default to outpatient
    procedureCodes: [],
    totalReimbursement: 0,
    totalImplantCost: 0,
    grandTotal: 0,
    createdDate: now,
    lastModified: now,
  };
};

/**
 * Initialize a new estimate
 */
export const initializeEstimate = (
  state: ContractEstimateSliceState,
  action: PayloadAction<{ company: string }>
) => {
  state.currentEstimate = createDefaultEstimate(action.payload.company);
  state.isDirty = false;
  state.validationErrors = {};
};

/**
 * Set the current estimate
 */
export const setCurrentEstimate = (
  state: ContractEstimateSliceState,
  action: PayloadAction<ContractEstimate | null>
) => {
  state.currentEstimate = action.payload;
  state.isDirty = false;
  state.validationErrors = {};
};

/**
 * Update estimate basic info (centerId, serviceDate, payerId, etc.)
 */
export const updateEstimate = (
  state: ContractEstimateSliceState,
  action: PayloadAction<{
    centerId?: string;
    centerCode?: string;
    centerName?: string;
    centerType?: string;
    serviceDate?: string;
    payerId?: string;
    payerName?: string;
    stayType?: StayType;
    notes?: string;
  }>
) => {
  if (state.currentEstimate) {
    Object.assign(state.currentEstimate, action.payload);
    state.currentEstimate.lastModified = new Date().toISOString();
    // Clear calculation results when inputs change
    state.currentEstimate.calculationResult = undefined;
    state.isDirty = true;
  }
};

/**
 * Clear the current estimate
 */
export const clearEstimate = (state: ContractEstimateSliceState) => {
  state.currentEstimate = null;
  state.isDirty = false;
  state.validationErrors = {};
};

/**
 * Save current estimate to saved estimates
 */
export const saveEstimate = (state: ContractEstimateSliceState) => {
  if (state.currentEstimate) {
    const estimateToSave = {
      ...state.currentEstimate,
      lastModified: new Date().toISOString(),
    };

    const existingIndex = state.savedEstimates.findIndex(
      (est) => est.id === estimateToSave.id
    );
    if (existingIndex !== -1) {
      state.savedEstimates[existingIndex] = estimateToSave;
    } else {
      state.savedEstimates.push(estimateToSave);
    }

    state.isDirty = false;
  }
};

/**
 * Load a saved estimate as the current estimate
 */
export const loadEstimate = (
  state: ContractEstimateSliceState,
  action: PayloadAction<string>
) => {
  const estimate = state.savedEstimates.find((est) => est.id === action.payload);
  if (estimate) {
    state.currentEstimate = { ...estimate };
    state.isDirty = false;
    state.validationErrors = {};
  }
};

/**
 * Delete a saved estimate
 */
export const deleteEstimate = (
  state: ContractEstimateSliceState,
  action: PayloadAction<string>
) => {
  state.savedEstimates = state.savedEstimates.filter(
    (est) => est.id !== action.payload
  );
};

/**
 * Add a saved estimate
 */
export const addSavedEstimate = (
  state: ContractEstimateSliceState,
  action: PayloadAction<ContractEstimate>
) => {
  const existingIndex = state.savedEstimates.findIndex(
    (est) => est.id === action.payload.id
  );
  if (existingIndex !== -1) {
    state.savedEstimates[existingIndex] = action.payload;
  } else {
    state.savedEstimates.push(action.payload);
  }
};

/**
 * Remove a saved estimate (alias for deleteEstimate)
 */
export const removeSavedEstimate = (
  state: ContractEstimateSliceState,
  action: PayloadAction<string>
) => {
  state.savedEstimates = state.savedEstimates.filter(
    (est) => est.id !== action.payload
  );
};

/**
 * Set all saved estimates
 */
export const setSavedEstimates = (
  state: ContractEstimateSliceState,
  action: PayloadAction<ContractEstimate[]>
) => {
  state.savedEstimates = action.payload;
};
